import React, { createContext, useContext, useState, useEffect } from 'react';

// This context provides white-labeling configurations
// The user object would typically come from an AuthContext

interface ThemeContextType {
    isBranded: boolean;
    brandLogo: string;
    brandName: string;
    role: string;
    setTheme: (user: any, parentOrg?: any) => void;
}

const ThemeContext = createContext<ThemeContextType>({
    isBranded: false,
    brandLogo: '/default-logo.png', // UdyamWell default
    brandName: 'UdyamWell',
    role: 'SELLER',
    setTheme: () => {}
});

// Helper: compute theme values from a user object (pure, no setState)
function computeTheme(user: any, parentOrg?: any) {
    if (!user) return null;
    if (user.role === 'ORGANISATION') {
        return {
            isBranded: true,
            brandLogo: user.businessProfilePic || user.profilePath || '',
            brandName: user.businessName || user.name || 'Enterprise Portal',
            role: 'ORGANISATION',
        };
    } else if (user.role === 'SELLER' && parentOrg && parentOrg.isBranded) {
        return {
            isBranded: true,
            brandLogo: parentOrg.businessProfilePic || parentOrg.profilePath || '',
            brandName: parentOrg.businessName || 'Seller Dashboard',
            role: 'SELLER',
        };
    } else {
        return {
            isBranded: false,
            brandLogo: '/default-logo.png',
            brandName: 'UdyamWell',
            role: user.role || 'SELLER',
        };
    }
}

// Synchronously read initial theme from localStorage — no flicker
function getInitialTheme() {
    try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            const theme = computeTheme(user);
            if (theme) return theme;
        }
    } catch (e) {}
    return { isBranded: false, brandLogo: '/default-logo.png', brandName: 'UdyamWell', role: 'SELLER' };
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const initial = getInitialTheme();
    const [isBranded, setIsBranded] = useState(initial.isBranded);
    const [brandLogo, setBrandLogo] = useState(initial.brandLogo);
    const [brandName, setBrandName] = useState(initial.brandName);
    const [role, setRole] = useState(initial.role);

    const setTheme = (user: any, parentOrg?: any) => {
        if (!user) return;
        const theme = computeTheme(user, parentOrg);
        if (!theme) return;
        setIsBranded(theme.isBranded);
        setBrandLogo(theme.brandLogo);
        setBrandName(theme.brandName);
        setRole(theme.role);
    };

    return (
        <ThemeContext.Provider value={{ isBranded, brandLogo, brandName, role, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
